import React, { useRef, useCallback, useEffect } from 'react';

/**
 * Simple rich text editor using contentEditable.
 * Produces HTML that can be stored in description/content fields.
 * Supports pasting formatted content from Word, ChatGPT, Google Docs, etc.
 * 
 * Props:
 *  - value: HTML string
 *  - onChange: (htmlString) => void
 *  - placeholder: string
 *  - rows: approximate height in rows (each row ~24px)
 */
const RichTextEditor = ({ value, onChange, placeholder, rows = 4 }) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Sync external value changes into the editor (only when value differs)
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // =========================================================================
  // PASTE HANDLER - cleans Word / ChatGPT / Google Docs markup
  // =========================================================================
  const handlePaste = useCallback((e) => {
    // Check if clipboard has HTML content
    const clipboardHtml = e.clipboardData?.getData('text/html');
    const clipboardText = e.clipboardData?.getData('text/plain');

    if (!clipboardHtml) {
      // No HTML on clipboard - let the browser handle plain text paste normally
      return;
    }

    e.preventDefault();

    const cleaned = cleanPastedHtml(clipboardHtml);

    // Insert the cleaned HTML at the current cursor position
    document.execCommand('insertHTML', false, cleaned);
    handleInput();
  }, [handleInput]);

  const execCommand = (command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleInput();
  };

  const toolbarBtnStyle = {
    background: 'none',
    border: '1px solid var(--gray-200, #e5e7eb)',
    borderRadius: '4px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: 1,
    minWidth: '30px',
    color: 'var(--gray-700, #374151)',
    transition: 'background 0.15s'
  };

  return (
    <div style={{ border: '1px solid var(--gray-300, #d1d5db)', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '6px 8px',
        borderBottom: '1px solid var(--gray-200, #e5e7eb)',
        background: 'var(--gray-50, #f9fafb)',
        flexWrap: 'wrap'
      }}>
        <button type="button" style={{ ...toolbarBtnStyle, fontWeight: 'bold' }} onClick={() => execCommand('bold')} title="Bold">
          B
        </button>
        <button type="button" style={{ ...toolbarBtnStyle, fontStyle: 'italic' }} onClick={() => execCommand('italic')} title="Italic">
          I
        </button>
        <button type="button" style={{ ...toolbarBtnStyle, textDecoration: 'underline' }} onClick={() => execCommand('underline')} title="Underline">
          U
        </button>
        <span style={{ borderLeft: '1px solid var(--gray-300, #d1d5db)', margin: '0 4px' }} />
        <button type="button" style={toolbarBtnStyle} onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
          • List
        </button>
        <button type="button" style={toolbarBtnStyle} onClick={() => execCommand('insertOrderedList')} title="Numbered List">
          1. List
        </button>
        <span style={{ borderLeft: '1px solid var(--gray-300, #d1d5db)', margin: '0 4px' }} />
        <button type="button" style={toolbarBtnStyle} onClick={() => execCommand('formatBlock', 'p')} title="Paragraph">
          ¶
        </button>
        <button type="button" style={toolbarBtnStyle} onClick={() => execCommand('removeFormat')} title="Clear Formatting">
          ✕
        </button>
      </div>

      {/* Editable Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder || 'Enter content...'}
        style={{
          minHeight: `${(rows || 4) * 24}px`,
          padding: '10px 12px',
          outline: 'none',
          fontSize: '14px',
          lineHeight: '1.6',
          color: 'var(--gray-800, #1f2937)',
          overflowY: 'auto',
          maxHeight: '300px'
        }}
        suppressContentEditableWarning
      />

      {/* Placeholder CSS via style tag */}
      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: var(--gray-400, #9ca3af);
          pointer-events: none;
        }
        [contenteditable] ul, [contenteditable] ol {
          padding-left: 20px;
          margin: 4px 0;
        }
        [contenteditable] li {
          margin-bottom: 2px;
        }
        [contenteditable] p {
          margin: 4px 0;
        }
      `}</style>
    </div>
  );
};

// =========================================================================
// HTML PASTE CLEANER
// Strips Word/Office/Google Docs junk while preserving meaningful formatting.
// =========================================================================
function cleanPastedHtml(html) {
  // Use a temporary DOM element to parse and clean
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // 1. Remove all <style>, <meta>, <link>, <title>, <script>, <xml> tags entirely
  const removeTags = temp.querySelectorAll('style, meta, link, title, script, xml, o\\:p');
  removeTags.forEach(el => el.remove());

  // 2. Remove Word-specific XML namespace elements (e.g., <o:p>, <w:sdt>)
  temp.innerHTML = temp.innerHTML
    .replace(/<\/?o:[^>]*>/gi, '')
    .replace(/<\/?w:[^>]*>/gi, '')
    .replace(/<\/?m:[^>]*>/gi, '')
    .replace(/<\/?v:[^>]*>/gi, '');

  // 3. Remove all comments (Word loves conditional comments)
  temp.innerHTML = temp.innerHTML.replace(/<!--[\s\S]*?-->/g, '');

  // 4. Walk all elements: strip classes, strip non-essential inline styles,
  //    remove empty spans, and convert some Word patterns to clean HTML
  const allowedTags = new Set([
    'p', 'br', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'sub', 'sup', 'blockquote',
    'table', 'thead', 'tbody', 'tr', 'td', 'th', 'div', 'span', 'hr'
  ]);

  // Inline style properties worth keeping
  const keepStyleProps = new Set([
    'font-weight', 'font-style', 'text-decoration', 'text-decoration-line'
  ]);

  function cleanNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return;
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.remove();
      return;
    }

    // Recurse into children FIRST (snapshot to avoid mutation issues)
    const children = [...node.childNodes];
    for (const child of children) {
      cleanNode(child);
    }

    const tagName = node.tagName.toLowerCase();

    // Remove completely if not in the allowed list and not a known container
    if (!allowedTags.has(tagName)) {
      // Unwrap: keep child content but ditch the element itself
      while (node.firstChild) {
        node.parentNode.insertBefore(node.firstChild, node);
      }
      node.remove();
      return;
    }

    // Remove all attributes except href on <a> tags
    const attrs = [...node.attributes];
    for (const attr of attrs) {
      if (tagName === 'a' && attr.name === 'href') continue;
      if (attr.name === 'style') continue; // handle style separately below
      node.removeAttribute(attr.name);
    }

    // Clean inline styles: keep only formatting-related properties
    if (node.style && node.getAttribute('style')) {
      const keptStyles = [];
      for (const prop of keepStyleProps) {
        const val = node.style.getPropertyValue(prop);
        if (val) {
          keptStyles.push(`${prop}: ${val}`);
        }
      }
      if (keptStyles.length > 0) {
        node.setAttribute('style', keptStyles.join('; '));
      } else {
        node.removeAttribute('style');
      }
    }

    // Remove empty spans (Word wraps everything in styled spans)
    if (tagName === 'span' && !node.getAttribute('style') && !node.hasChildNodes()) {
      node.remove();
      return;
    }

    // Unwrap bare spans with no remaining styles
    if (tagName === 'span' && !node.getAttribute('style')) {
      while (node.firstChild) {
        node.parentNode.insertBefore(node.firstChild, node);
      }
      node.remove();
      return;
    }
  }

  // Clean all nodes
  const children = [...temp.childNodes];
  for (const child of children) {
    cleanNode(child);
  }

  // 5. Convert inline styles to semantic tags for cleaner storage
  let result = temp.innerHTML;

  // Convert font-weight bold styles to <strong>
  result = result.replace(
    /<span style="[^"]*font-weight:\s*(?:bold|[7-9]00)[^"]*">([\s\S]*?)<\/span>/gi,
    '<strong>$1</strong>'
  );

  // Convert font-style italic styles to <em>
  result = result.replace(
    /<span style="[^"]*font-style:\s*italic[^"]*">([\s\S]*?)<\/span>/gi,
    '<em>$1</em>'
  );

  // Convert text-decoration underline styles to <u>
  result = result.replace(
    /<span style="[^"]*text-decoration(?:-line)?:\s*underline[^"]*">([\s\S]*?)<\/span>/gi,
    '<u>$1</u>'
  );

  // 6. Clean up empty paragraphs and excessive whitespace
  result = result
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
    .replace(/^\s+|\s+$/g, '');

  return result;
}

export default RichTextEditor;
